// Main application component - Journal Party v7 "Neon Party"
import { QueryClientProvider, QueryClient } from "@tanstack/react-query";
import { Suspense, lazy, startTransition, useContext, useEffect, useLayoutEffect, useState } from "react";
import type { ReactNode } from "react";
import { Helmet, HelmetProvider } from "react-helmet-async";
import { Routes, Route, BrowserRouter, Navigate, useLocation, useParams } from "react-router-dom";
import * as Sentry from "@sentry/react";
import { Capacitor } from "@capacitor/core";
import { Toaster as SonnerToaster } from "sonner";
import ProtectedRoute from "./components/ProtectedRoute";
import SubscriptionRequiredRoute from "./components/SubscriptionRequiredRoute";
import { ProgramAuthoringRoute } from "./components/routing/ProgramAuthoringRoute";
import { CoreAccessRoute, NativeShellCoreAccessGate } from "@/components/routing/CoreAccessRoute";
import { EXACT_PAGE_TITLES, enforceBrandSuffixLimit } from "@/lib/seoTitle";
import { JourneyAuthoringRoute } from "@/components/routing/JourneyAuthoringRoute";
import { CoreAccessAuthSync } from "@/hooks/useCoreAccess";
import { MinVersionGate } from "@/components/routing/MinVersionGate";
import JourneySessionAccessRoute from "./components/JourneySessionAccessRoute";
import { CourseWritingPreviewBoundary } from "@/components/journeys/course/CoursePreviewContext";
import ErrorBoundary from "@/components/ErrorBoundary";
import { AuthProvider, AuthContext } from "@/hooks/useAuth";
import { ThemeProvider, useTheme } from "@/hooks/useTheme";
import { JpThemeProvider } from "@/hooks/useJpTheme";
import { VipPlanNameProvider } from "@/components/VipPlanNameProvider";
import { seedRemoteFlagCache } from "@/hooks/useRemoteFlag";
import { Toaster } from "@/components/ui/toaster";
import { ToastMark } from "@/components/composites/Toast";
import { CookieConsentBanner } from "@/components/composites/CookieConsentBanner";
import { ConfirmEmailBanner } from "@/components/composites/ConfirmEmailBanner";
import { SITE_URL } from "@/components/composites/SEO";
import { MarketingScripts } from "@/components/composites/MarketingScripts";
import { CharlaWidget } from "@/components/integrations/CharlaWidget";
import { NativeTrackingBootstrap } from "@/components/NativeTrackingBootstrap";
import { NeonLoadingRing } from "@/components/feedback/NeonLoadingRing";
import RoutePrefetcher from "@/components/performance/RoutePrefetcher";
import { getLegacyEventRedirectPath } from '@/routes/legacyEventRedirect';
import { ShortShareRedirect } from '@/routes/shortShareRedirect';
import { NativeRouteFence } from '@/routes/nativeRouteFence';
import { getJourneyFunnelRouteTitle, matchJourneyFunnelPath } from '@/config/journeyFunnelRoutes';
import { CREATOR_AUDIENCE_ROUTES, creatorAudiencePath } from '@/pages/creatorAudienceRoutes';
import { NativeAccountReminder } from '@/components/NativeAccountReminder';
import {
  readNativeAccountCompletion,
  resolveNativeRootDestination,
} from '@/utils/nativeAccountCompletion';

import { AcquisitionCapture } from "@/components/AcquisitionCapture";
import { trackPageView } from "@/utils/trackingManager";
import { scrubCheckoutReturnParamsFromUrl } from "@/utils/checkoutReturnScrub";
import { isCourseWritingPreviewRoute, isJourneyManagedTrackingRoute } from "@/utils/journeyRouteTracking";
import { noteJourneyRoute } from "@/utils/journeyTiming";
import { getNativeDeviceProfile } from "@/utils/nativeDevice";
import { AppSsrDataProvider, SsrDataNavigationObserver, type AppSsrData } from "@/lib/ssrPageData";
import { preloadFirstPaintImage } from "@/utils/preloadFirstPaintImage";
import { AdminUserRedirect, AdvisoryBoardRedirect, EmailsRedirect, ProgramsListRedirect } from "@/components/admin/shell/adminRedirects";

// v7 Pages
// VIP/Pro Phase 4: the rescue host (and its sheet) load after first paint.
const VipRescueHost = lazy(() =>
  import("./components/composites/VipRescueHost").then((m) => ({ default: m.VipRescueHost })),
);
const Homepage = lazy(() => import("./pages/Homepage"));
const GuidedSessionLanding = lazy(() => import("./pages/GuidedSessionLanding"));
const Auth = lazy(() => import("./pages/Auth"));
const AuthCallback = lazy(() => import("./pages/AuthCallback"));
const AuthNativeCallback = lazy(() => import("./pages/AuthNativeCallback"));
const AuthConfirmEmail = lazy(() => import("./pages/AuthConfirmEmail"));
const AuthVerify = lazy(() => import("./pages/AuthVerify"));
const NativeWelcome = lazy(() => import("./pages/NativeWelcome"));
const CompleteAccount = lazy(() => import("./pages/CompleteAccount"));
const CheckEmail = lazy(() => import("./pages/CheckEmail"));
const Calendar = lazy(() => import("./pages/Calendar"));
// EventRoom removed — in-app live rooms disabled (YouTube-centric pivot)
// const EventRoom = lazy(() => import("./pages/EventRoom"));
const Profile = lazy(() => import("./pages/Profile"));
const Settings = lazy(() => import("./pages/Settings"));
const Prompts = lazy(() => import("./pages/Prompts")); // Legacy - redirects to /programs
const Programs = lazy(() => import("./pages/Programs")); // Programs discovery (/programs)
const LibraryIndex = lazy(() => import("./pages/LibraryIndex")); // Crawlable HTML site index (/library)
const ProgramDetail = lazy(() => import("./pages/ProgramDetail"));
const SoloSessionJp = lazy(() => import("./pages/SoloSessionJp"));
const DailyPromptSession = lazy(() => import("./pages/DailyPromptSession"));
const FreestyleSessionJp = lazy(() => import("./pages/FreestyleSessionJp"));
// /onboarding decides once between onboarding v3 and the legacy flow.
const OnboardingEntry = lazy(async () => {
  const [module] = await Promise.all([
    import("./pages/OnboardingEntry"),
    preloadFirstPaintImage('/mascots/party-animals-group.webp'),
  ]);
  return module;
});
// Dev-only click-through of onboarding v3 without an account or profile
// writes. import.meta.env.DEV folds to false in production builds, so neither
// the route nor this chunk ships.
const OnboardingV3Preview = import.meta.env.DEV
  ? lazy(async () => {
      const module = await import("./pages/OnboardingV3");
      return { default: () => <module.OnboardingV3 preview /> };
    })
  : null;
// Dev-only preview of the session-complete celebration (never ships).
const SessionCompletePreview = import.meta.env.DEV
  ? lazy(async () => {
      const module = await import("./components/composites/SessionCompleteJp");
      return { default: () => <module.SessionCompleteJp title="Writing complete" subtitle="Feel Calmer: Day 1" primaryAction={{ label: "Continue", onClick: () => window.location.reload() }} /> };
    })
  : null;
// Dev-only gallery of toast types (never ships).
const ToastGallery = import.meta.env.DEV
  ? lazy(async () => {
      const { Toast } = await import("./components/composites/Toast");
      return {
        default: () => (
          <div className="grid min-h-screen content-center gap-3 bg-jp-bg-page p-6">
            <Toast variant="success" title="You're set for Wednesday" description="A push the day before and one an hour before the party." onClose={() => undefined} />
            <Toast title="Weekly reminder off" action={<button type="button">Undo</button>} onClose={() => undefined} />
            <Toast variant="warning" title="Reminder saved, but nothing can reach you yet" description="Turn on push or email reminders in Settings." onClose={() => undefined} />
            <Toast variant="error" title="Couldn't set reminder" description="Check your connection and try again." onClose={() => undefined} />
          </div>
        ),
      };
    })
  : null;
const SessionStartPreview = import.meta.env.DEV ? lazy(() => import("./dev/SessionStartPreview")) : null;
const SessionPlayerPreview = import.meta.env.DEV ? lazy(() => import("./dev/SessionPlayerPreview")) : null;
const HomePreview = import.meta.env.DEV ? lazy(() => import("./dev/HomePreview")) : null;
const PartyPreview = import.meta.env.DEV ? lazy(() => import("./dev/PartyPreview")) : null;
const PartyLinesPreview = import.meta.env.DEV ? lazy(() => import("./dev/PartyLinesPreview")) : null;
const Pricing = lazy(() => import("./pages/Pricing"));
const NativeUpgrade = lazy(() => import("./pages/NativeUpgrade"));
const FreePrograms = lazy(() => import("./pages/FreePrograms"));
const Links = lazy(() => import("./pages/Links"));
const PremiumWelcome = lazy(() => import("./pages/PremiumWelcome"));
// Physical bundle offer archived Oct 13, 2026: see src/archive/physical-bundle/README.md
const PhysicalBundleThankYou = lazy(() => import("./pages/PhysicalBundleThankYou"));
const JournalPartyLiveFunnel = lazy(() => import("./pages/JournalPartyLiveFunnel"));
const JournalPartyLiveCheckEmail = lazy(() => import("./pages/JournalPartyLiveCheckEmail"));
const JournalPartyLiveEmailConfirmation = lazy(() => import("./pages/JournalPartyLiveEmailConfirmation"));
const JournalPartyLiveWelcome = lazy(() => import("./pages/JournalPartyLiveWelcome"));
const JournalPartyLiveOffer = lazy(() => import("./pages/JournalPartyLiveOffer"));
const JournalPartyLiveChallenge = lazy(() => import("./pages/JournalPartyLiveChallenge"));
const ChallengePremiumOffer = lazy(() => import("./pages/ChallengePremiumOffer"));
const ChallengePremiumDownsellOffer = lazy(() => import("./pages/ChallengePremiumDownsellOffer"));
const ChallengeOrderConfirmation = lazy(() => import("./pages/ChallengeOrderConfirmation"));
const JourneyFunnelLanding = lazy(() => import("./pages/JourneyFunnelLanding"));
const JourneyFunnelPremiumOffer = lazy(() => import("./pages/JourneyFunnelPremiumOffer"));
const JourneyFunnelDownsell = lazy(() => import("./pages/JourneyFunnelDownsell"));
const JourneyFunnelConfirmation = lazy(() => import("./pages/JourneyFunnelConfirmation"));

// Legal & Info Pages (Phase 26)
const Terms = lazy(() => import("./pages/Terms"));
const Privacy = lazy(() => import("./pages/Privacy"));
const About = lazy(() => import("./pages/About"));
const Contact = lazy(() => import("./pages/Contact"));
const Support = lazy(() => import("./pages/Support"));
const AdvisoryBoard = lazy(() => import("./pages/AdvisoryBoard"));

// Mental Health Advisory Board - Advisor Portal (Phase 53)
const AdvisorPortal = lazy(() => import("./pages/AdvisorPortal"));
const ReviewDetail = lazy(() => import("./pages/ReviewDetail"));

// Nav v2 Pages (Home | Create | Live)
const Home = lazy(() => import("./pages/Home"));
const Live = lazy(() => import("./pages/Live")); // Events discovery (/live)
const Party = lazy(() => import("./pages/Party")); // The member's weekly party (/party)
const Replays = lazy(() => import("./pages/Replays")); // YouTube replays (/replays)

// My Programs & Creator Tools
const MyPrograms = lazy(() => import("./pages/MyPrograms"));
const Create = lazy(() => import("./pages/Create"));
const CreateProgram = lazy(() => import("./pages/CreateProgram"));
const CreateProgramPrompts = lazy(() => import("./pages/CreateProgramPrompts"));
// CreateEvent removed — user event creation disabled (YouTube-centric pivot)
// const CreateEvent = lazy(() => import("./pages/CreateEvent"));

// Creator Analytics — /creator now redirects to /create (unified hub)

// Public Landing Pages (Epic 17)
const ProgramLanding = lazy(() => import("./pages/ProgramLanding"));
// CourseLandingPage removed — courses deprecated, redirects to /programs
// ChallengeLandingPage removed — challenges replaced by journeys, redirects to /programs
const EventLanding = lazy(() => import("./pages/EventLanding"));
const JourneyEntry = lazy(() => import("./pages/JourneyEntry"));
const JourneyLanding = lazy(() => import("./pages/GuidedJourneyLandingRoute"));
// One component for /for-creators and every audience page, so moving between
// them keeps the page mounted. It also loads the audience copy.
const ForCreators = lazy(() => import("./pages/ForCreatorsAudience"));
const JournalPromptsHub = lazy(() => import("./pages/JournalPromptsHub"));
const DailyJournalPrompts = lazy(() => import("./pages/DailyJournalPrompts"));
const WhatToWriteInAJournal = lazy(() => import("./pages/WhatToWriteInAJournal"));
const JournalPromptOfTheDay = lazy(() => import("./pages/JournalPromptOfTheDay"));
const JournalPromptGenerator = lazy(() => import("./pages/JournalPromptGenerator"));
const JournalPromptTagPage = lazy(() => import("./pages/JournalPromptTagPage"));
const JournalPromptPersonaPage = lazy(() => import("./pages/JournalPromptPersonaPage"));
const JournalPromptsMonthPage = lazy(() => import("./pages/JournalPromptsMonthPage"));
const NewYearJournalPrompts = lazy(() => import("./pages/NewYearJournalPrompts"));
const GuidedJournalingGuide = lazy(() => import("./pages/GuidedJournalingGuide"));
const GuidedJournalingApp = lazy(() => import("./pages/GuidedJournalingApp"));
const PrivateJournalingApp = lazy(() => import("./pages/PrivateJournalingApp"));
const AiJournalingAlternative = lazy(() => import("./pages/AiJournalingAlternative"));
const TherapistReviewedJournalPrompts = lazy(() => import("./pages/TherapistReviewedJournalPrompts"));
const BestGuidedJournalingPlatform = lazy(() => import("./pages/BestGuidedJournalingPlatform"));
const BestJournalingApps = lazy(() => import("./pages/BestJournalingApps"));
const FaqCompletelyFreeJournalApps = lazy(() => import("./pages/FaqCompletelyFreeJournalApps"));
const FaqDoesAppleHaveJournalingApp = lazy(() => import("./pages/FaqDoesAppleHaveJournalingApp"));
const JournalPromptsForAnxiety = lazy(() => import("./pages/JournalPromptsForAnxiety"));
const JournalingForDepression = lazy(() => import("./pages/JournalingForDepression"));
const GratitudeJournaling = lazy(() => import("./pages/GratitudeJournaling"));
const ThingsToBeGratefulFor = lazy(() => import("./pages/ThingsToBeGratefulFor"));
const GratitudeJournalApp = lazy(() => import("./pages/GratitudeJournalApp"));
const JournalPromptsForMentalHealth = lazy(() => import("./pages/JournalPromptsForMentalHealth"));
const BestJournalingAppWithPrompts = lazy(() => import("./pages/BestJournalingAppWithPrompts"));
const JournalPromptsForSelfGrowth = lazy(() => import("./pages/JournalPromptsForSelfGrowth"));
const JournalingTechniques = lazy(() => import("./pages/JournalingTechniques"));
const JournalingIdeas = lazy(() => import("./pages/JournalingIdeas"));
const GroupJournaling = lazy(() => import("./pages/GroupJournaling"));
const JournalingClub = lazy(() => import("./pages/JournalingClub"));
const JournalSessions = lazy(() => import("./pages/JournalSessions"));
const JournalingChallenge = lazy(() => import("./pages/JournalingChallenge"));
const FaqAppDailyJournalPrompts = lazy(() => import("./pages/FaqAppDailyJournalPrompts"));
const FaqBestAppForJournaling = lazy(() => import("./pages/FaqBestAppForJournaling"));
const Faq333JournalMethod = lazy(() => import("./pages/Faq333JournalMethod"));
const FaqWhatToJournalEveryDay = lazy(() => import("./pages/FaqWhatToJournalEveryDay"));
const FaqWhatToWriteMentalHealth = lazy(() => import("./pages/FaqWhatToWriteMentalHealth"));
const Faq5MinuteJournalPrompts = lazy(() => import("./pages/Faq5MinuteJournalPrompts"));
const Faq321JournalingMethod = lazy(() => import("./pages/Faq321JournalingMethod"));
const FaqWhatToWriteInJournalEveryMorning = lazy(() => import("./pages/FaqWhatToWriteInJournalEveryMorning"));
const FaqGoodMorningJournalPrompts = lazy(() => import("./pages/FaqGoodMorningJournalPrompts"));
const ThreeHundredSixtyFiveDailyJournalPrompts = lazy(() => import("./pages/ThreeHundredSixtyFiveDailyJournalPrompts"));

// Podcast Pages
const Podcast = lazy(() => import("./pages/Podcast"));
const PodcastEpisode = lazy(() => import("./pages/PodcastEpisode"));

// Social Features (Epic 13)
const UserProfile = lazy(() => import("./pages/UserProfile"));

// User Schedule (My RSVPs redesign)
const MySchedule = lazy(() => import("./pages/MySchedule"));

// Gamification (Epic 3)

// Collections (Epic 10.2)
const CollectionDetail = lazy(() => import("./pages/CollectionDetail"));
const DiscoveryPrototype = lazy(() => import("./pages/DiscoveryPrototype"));

// Email Unsubscribe (Epic 19)
const Unsubscribe = lazy(() => import("./pages/Unsubscribe"));

// Courses/challenges removed — multi-day content now uses Journeys.

// Journeys
const JourneyDetail = lazy(() => import("./pages/GuidedJourneyDetailRoute"));
const JourneyLearnJp = lazy(() => import("./pages/GuidedJourneyLearn"));
const CourseJourneyEdit = lazy(() => import("./pages/CourseJourneyEdit"));
const CreatorJourneys = lazy(() => import("./pages/CreatorJourneys"));

// 404 Page
const NotFound = lazy(() => import("./pages/NotFound"));

// Admin Pages (Epic 9)
const AdminLayout = lazy(() => import("./layouts/AdminLayout"));
const AdminToday = lazy(() => import("./pages/admin/AdminToday"));
const AdminLibrary = lazy(() => import("./pages/admin/AdminLibrary"));
const AdminEvents = lazy(() => import("./pages/admin/AdminEvents"));
const AdminEventEdit = lazy(() => import("./pages/admin/AdminEventEdit"));
const AdminPeople = lazy(() => import("./pages/admin/AdminPeople"));
const AdminProgramEdit = lazy(() => import("./pages/admin/AdminProgramEdit"));
const AdminGrowth = lazy(() => import("./pages/admin/AdminGrowth"));
const AdminMessages = lazy(() => import("./pages/admin/AdminMessages"));
const AdminMoney = lazy(() => import("./pages/admin/AdminMoney"));
const AdminSettings = lazy(() => import("./pages/admin/AdminSettings"));
const AdminCollections = lazy(() => import("./pages/admin/AdminCollections"));
const AdminReports = lazy(() => import("./pages/admin/AdminReports"));
const AdminMarketing = lazy(() => import("./pages/admin/AdminMarketing"));

// Admin Journeys
const AdminJourneys = lazy(() => import("./pages/admin/AdminJourneys"));
const AdminJourneyEdit = lazy(() => import("./pages/admin/AdminJourneyEdit"));

// Redirect /share/program/:slug → /p/:slug
function ShareProgramRedirect() {
  const { slug } = useParams();
  return <Navigate to={`/p/${slug}`} replace />;
}

// Redirect /share/event/:slug → /e/:slug
function ShareEventRedirect() {
  const { slug } = useParams();
  return <Navigate to={`/e/${slug}`} replace />;
}

// Redirect /share/journey/:slug → /j/:slug
function ShareJourneyRedirect() {
  const { slug } = useParams();
  return <Navigate to={`/j/${slug}`} replace />;
}

const FILL_YOUR_JOURNAL_LEGACY_CHALLENGE_REFS = new Set([
  '4797ab0d-7967-46c7-b78f-e97a900950b5',
  '21-day-fill-your-journal-challenge-4797ab',
]);
const FILL_YOUR_JOURNAL_JOURNEY_SLUG = '21-day-fill-your-journal-challenge-4797ab-journey';

function LegacyChallengeRedirect({ learn = false }: { learn?: boolean }) {
  const { slug, challengeId } = useParams();
  const location = useLocation();
  const ref = slug || challengeId || '';
  const suffix = `${location.search || ''}${location.hash || ''}`;

  if (FILL_YOUR_JOURNAL_LEGACY_CHALLENGE_REFS.has(ref)) {
    const path = learn
      ? `/journey/${FILL_YOUR_JOURNAL_JOURNEY_SLUG}/learn`
      : `/j/${FILL_YOUR_JOURNAL_JOURNEY_SLUG}`;
    return <Navigate to={`${path}${suffix}`} replace />;
  }

  return <Navigate to="/programs" replace />;
}

function LegacyPostRedirect() {
  const { slug } = useParams();
  const location = useLocation();
  const suffix = `${location.search || ''}${location.hash || ''}`;

  if (slug === 'turn-your-dreams-to-reality-by-writing-it-down-8-tips') {
    return <Navigate to={`/podcast/27${suffix}`} replace />;
  }

  return <Navigate to={`/journal-prompts${suffix}`} replace />;
}

// Preserve location state (e.g., fromOnboarding) when redirecting legacy /discover -> /home.
function LegacyDiscoverRedirect() {
  const location = useLocation();
  return <Navigate to="/home" replace state={location.state} />;
}

function LegacyEventsRedirect() {
  const auth = useContext(AuthContext);
  const user = auth?.user ?? null;
  const loading = auth?.loading ?? false;
  const { eventId } = useParams();
  const location = useLocation();
  if (loading) {
    return <RouteFallback />;
  }

  const destination = getLegacyEventRedirectPath({
    eventId,
    isAuthenticated: Boolean(user),
    search: location.search || '',
    hash: location.hash || '',
  });

  return <Navigate to={destination} replace />;
}

function NativeAwareRootRoute() {
  const auth = useContext(AuthContext);
  const user = auth?.user ?? null;
  const loading = auth?.loading ?? false;
  const profile = auth?.profile ?? null;
  const profileLoading = auth?.profileLoading ?? false;
  const isNativeRuntime = Capacitor.isNativePlatform();

  if (!isNativeRuntime) {
    return <Homepage />;
  }

  if (loading || (user && profileLoading)) {
    return <RouteFallback />;
  }

  const destination = resolveNativeRootDestination({
    user,
    onboardingCompleted: Boolean(profile?.onboarding_completed_at),
    accountCompletion: readNativeAccountCompletion(user?.id),
  });
  if (destination) {
    return <Navigate to={destination} replace />;
  }

  return <NativeWelcome />;
}

/**
 * Releases the pre-paint returning-session cover after auth has resolved.
 *
 * At `/`, a confirmed user keeps the cover through the redirect so the
 * prerendered marketing page can never appear between auth restoration and
 * the dashboard. Once the destination route commits, the cover is removed.
 * A stale stored-session hint is also handled safely: signed-out visitors see
 * the homepage as soon as validation finishes.
 */
export function ReturningSessionGate() {
  const auth = useContext(AuthContext);
  const location = useLocation();
  const user = auth?.user ?? null;
  const loading = auth?.loading ?? false;

  useEffect(() => {
    if (typeof document === 'undefined') return;

    const root = document.documentElement;
    if (!root.classList.contains('jp-returning-session') || loading) return;

    if (location.pathname === '/' && user) {
      return;
    }

    root.classList.remove('jp-returning-session');
  }, [loading, location.pathname, user]);

  return null;
}

// If a logged-out user opens /program/:id, send them to the public landing page for that program.
function ProgramDetailRoute() {
  const auth = useContext(AuthContext);
  const user = auth?.user ?? null;
  const loading = auth?.loading ?? false;
  const { programId } = useParams();
  const location = useLocation();

  if (loading) {
    return <RouteFallback />;
  }

  if (!user) {
    const suffix = `${location.search || ''}${location.hash || ''}`;
    return <Navigate to={`/p/${programId || ''}${suffix}`} replace />;
  }

  return <ProgramDetail />;
}

// If a logged-out user opens /journey/:id, send them to the public journey landing page.
function JourneyDetailRoute() {
  const auth = useContext(AuthContext);
  const user = auth?.user ?? null;
  const loading = auth?.loading ?? false;
  const { journeyId } = useParams();
  const location = useLocation();

  if (loading) {
    return <RouteFallback />;
  }

  if (!user) {
    const suffix = `${location.search || ''}${location.hash || ''}`;
    return <Navigate to={`/j/${journeyId || ''}${suffix}`} replace />;
  }

  return <JourneyDetail />;
}

const ROUTE_TRACKING_TITLES: Record<string, string> = {
  '/start': 'Guided Journaling Sessions | Journal Party',
  '/home': 'Home | Journal Party',
  '/first-session': 'First Session | Journal Party',
  '/daily-prompt/session': 'Prompt of the Day | Journal Party',
  '/live': 'Live | Journal Party',
  '/replays': 'Replays | Journal Party',
  '/calendar': 'Calendar | Journal Party',
  '/programs': 'Guided Journal Programs to Write by Hand | Journal Party',
  '/profile': 'Profile | Journal Party',
  '/settings': 'Settings | Journal Party',
  '/schedule': 'Schedule | Journal Party',
  '/welcome-premium': 'Welcome to Premium | Journal Party',
  '/advisor-portal': 'Advisor Portal | Journal Party',
  '/unsubscribe': 'Unsubscribe | Journal Party',
  '/search': 'Search | Journal Party',
  '/my-programs': 'My Programs | Journal Party',
  '/create': 'Create | Journal Party',
  '/create/program': 'Create Program | Journal Party',
  '/create/journeys': 'My Journeys | Journal Party',
  '/pricing': 'Pricing | Journal Party',
  '/for-creators': 'Guided Journaling for Coaches & Creators | Journal Party',
  ...Object.fromEntries(CREATOR_AUDIENCE_ROUTES.map(page => [creatorAudiencePath(page.slug), page.seoTitle])),
  '/free': 'Free Guided Journal Programs | Journal Party',
  '/library': 'Browse the Full Program Library | Journal Party',
  '/daily-journal-prompts': 'Daily journal prompts: 66 questions to write on paper',
  '/what-to-write-in-a-journal': 'What to Write in a Journal | Journal Party',
  // /journal-prompt-of-the-day is omitted. Helmet sets a title that includes
  // today's Pacific date, and a static entry here would replace it.
  '/journal-prompt-generator': 'Random Journal Prompt Generator: Prompts by Theme',
  '/journal-prompts/for-teens': 'Journal Prompts for Teens: A Guide for Parents and Educators',
  '/journal-prompts/for-women': 'Journal Prompts for Women | Journal Party',
  '/journal-prompts/fun': 'Fun Journal Prompts | Journal Party',
  '/journal-prompts/august': 'August Journal Prompts: One for Every Day | Journal Party',
  '/journal-prompts/september': 'September Journal Prompts: One for Every Day | Journal Party',
  '/journal-prompts/october': 'October Journal Prompts: One for Every Day | Journal Party',
  '/journal-prompts/november': 'November Journal Prompts: One for Every Day | Journal Party',
  '/journal-prompts/december': 'December Journal Prompts: One for Every Day | Journal Party',
  '/journal-prompts/january': 'January Journal Prompts: One for Every Day | Journal Party',
  '/journal-prompts/new-year': 'New Year Journal Prompts: 41 Questions for 2027',
  '/faq/what-to-write-in-journal-every-morning': 'What Should I Write in My Journal Every Morning? | Journal Party',
  '/faq/good-morning-journal-prompts': 'What Are Good Morning Journal Prompts? | Journal Party',
  '/faq/what-can-i-journal-about-every-day': 'What Can I Journal About Every Day? 7 Repeatable Prompts | Journal Party',
  '/group-journaling': 'Group Journaling | Journal Party',
  '/journaling-club': 'Journaling Club: Join Free Live Sessions Online or Start Your Own | Journal Party',
  '/journaling-challenge': '30-Day Journaling Challenge (2026): Full Prompt Calendar + 21-Day Alternative | Journal Party',
  '/bundle/thanks': 'Bundle Order Confirmed | Journal Party',
  '/terms': 'Terms | Journal Party',
  '/privacy': 'Privacy | Journal Party',
  '/about': 'Our Story and Team | About Journal Party',
  '/contact': 'Contact | Journal Party',
  '/support': 'Support | Journal Party',
  '/auth/callback': 'Completing Sign In | Journal Party',
  '/journal-party-live': 'Journal Party Live | Journal Party',
  '/journal-party-live/check-email': 'Confirm Your Journal Party Live Reminder | Journal Party',
  '/journal-party-live/confirm': 'Journal Party Live Reminder Confirmation | Journal Party',
  '/journal-party-live/welcome': "You're In for Journal Party Live | Journal Party",
  '/journal-party-live/offer': 'Journal Party Premium: Guided Journaling On Paper | Journal Party',
  '/journal-party-live/challenge': '21-Day Fill Your Journal Challenge | Journal Party',
  '/challenge': '21-Day Fill Your Journal Challenge | Journal Party',
  '/journal-party-live/challenge/second-chance': '21-Day Fill Your Journal Challenge | Journal Party',
  '/journal-party-live/challenge/signup': 'Create Your Account | Journal Party',
  '/journal-party-live/challenge/premium-offer': 'One-time offer: a full year of Premium for $67 | Journal Party',
  '/challenge/premium-offer': 'One-time offer: a full year of Premium for $67 | Journal Party',
  '/journal-party-live/challenge/premium-downsell': '21-day Premium trial for your challenge | Journal Party',
  '/challenge/premium-downsell': '21-day Premium trial for your challenge | Journal Party',
  '/journal-party-live/challenge/confirmation': "You're in - 21-Day Fill Your Journal Challenge | Journal Party",
  '/challenge/confirmation': "You're in - 21-Day Fill Your Journal Challenge | Journal Party",
  // The spec's exact page titles stay the single source of truth.
  ...EXACT_PAGE_TITLES,
};

const ADMIN_ROUTE_TRACKING_TITLES: Record<string, string> = {
  dashboard: 'Admin Dashboard',
  library: 'Admin Library',
  programs: 'Admin Programs',
  collections: 'Admin Collections',
  events: 'Admin Events',
  people: 'Admin People',
  journeys: 'Admin Journeys',
  growth: 'Admin Growth',
  messages: 'Admin Messages',
  money: 'Admin Money',
  settings: 'Admin Settings',
  marketing: 'Admin Marketing',
};

const PAGE_VIEW_TECHNICAL_SKIP_PLATFORMS = ['ga4', 'meta', 'google_ads', 'tiktok'] as const;

const AUTH_ROUTE_TRACKING_SUPPRESSED_PATHS = new Set([
  '/auth/callback',
  '/auth/native-callback',
  '/auth/verify',
]);

const FUNNEL_PAGE_PATHS = new Set([
  '/start',
  '/journal-party-live',
  '/journal-party-live/check-email',
  '/journal-party-live/confirm',
  '/journal-party-live/welcome',
  '/journal-party-live/offer',
  '/journal-party-live/challenge',
  '/challenge',
  '/journal-party-live/challenge/second-chance',
  '/journal-party-live/challenge/signup',
  '/journal-party-live/challenge/premium-offer',
  '/challenge/premium-offer',
  '/journal-party-live/challenge/premium-downsell',
  '/challenge/premium-downsell',
  '/journal-party-live/challenge/confirmation',
  '/challenge/confirmation',
]);

function getRouteTrackingPageCategory(path: string): 'admin' | 'app' | 'public' | 'funnel' | 'technical' {
  const normalizedPath = (path.replace(/\/+$/, '') || '/').toLowerCase();

  if (normalizedPath.startsWith('/admin')) return 'admin';
  if (
    normalizedPath === '/auth' ||
    AUTH_ROUTE_TRACKING_SUPPRESSED_PATHS.has(normalizedPath) ||
    normalizedPath === '/check-email'
  ) {
    return 'technical';
  }
  if (FUNNEL_PAGE_PATHS.has(normalizedPath) || matchJourneyFunnelPath(normalizedPath)) return 'funnel';
  if (
    normalizedPath === '/' ||
    normalizedPath === '/for-creators' ||
    normalizedPath.startsWith('/for-creators/') ||
    normalizedPath.startsWith('/p/') ||
    normalizedPath.startsWith('/e/')
  ) {
    return 'public';
  }

  return 'app';
}

function getAppRouteTrackingTitle(path: string, search = '', state?: unknown): string | null {
  const normalizedPath = path.replace(/\/+$/, '') || '/';

  if (ROUTE_TRACKING_TITLES[normalizedPath]) {
    return enforceBrandSuffixLimit(ROUTE_TRACKING_TITLES[normalizedPath]);
  }

  // Journey funnels (/journal/<seoSlug>[/<step>]), web only.
  const journeyFunnelTitle = getJourneyFunnelRouteTitle(normalizedPath);
  if (journeyFunnelTitle) return journeyFunnelTitle;

  if (normalizedPath === '/auth') {
    const params = new URLSearchParams(search);
    const mode = params.get('mode');
    const type = params.get('type');
    if (mode === 'signup') return 'Create Your Account | Journal Party';
    if (mode === 'forgot') return 'Reset Your Password | Journal Party';
    if (mode === 'reset' || type === 'recovery') return 'Choose a New Password | Journal Party';
    return 'Sign In | Journal Party';
  }

  if (normalizedPath === '/check-email') {
    const checkEmailState = state && typeof state === 'object'
      ? (state as { isPasswordReset?: boolean; deliveryType?: string; purchaseSummary?: unknown })
      : null;
    if (checkEmailState?.isPasswordReset) return 'Reset Password Email Sent | Journal Party';
    if (checkEmailState?.deliveryType === 'magiclink' && checkEmailState.purchaseSummary) {
      return 'Order Confirmed | Journal Party';
    }
    if (checkEmailState?.deliveryType === 'magiclink') return 'Check Your Sign-In Email | Journal Party';
    return 'Verify Your Email | Journal Party';
  }

  if (normalizedPath.startsWith('/admin')) {
    const [, , section] = normalizedPath.split('/');
    const title = section ? ADMIN_ROUTE_TRACKING_TITLES[section] || 'Admin' : 'Admin';
    return `${title} | Journal Party`;
  }

  if (/^\/create\/program\/[^/]+\/prompts$/.test(normalizedPath)) {
    return 'Program Prompts | Journal Party';
  }
  if (/^\/create\/program\/[^/]+$/.test(normalizedPath)) {
    return 'Edit Program | Journal Party';
  }
  if (/^\/review\/[^/]+$/.test(normalizedPath)) {
    return 'Review | Journal Party';
  }
  if (/^\/(?:user|profile)\/[^/]+$/.test(normalizedPath)) {
    return 'Profile | Journal Party';
  }
  if (/^\/collection\/[^/]+$/.test(normalizedPath)) {
    return 'Collection | Journal Party';
  }
  if (/^\/program\/[^/]+$/.test(normalizedPath)) {
    return 'Program | Journal Party';
  }
  if (/^\/session\/[^/]+$/.test(normalizedPath)) {
    return 'Session | Journal Party';
  }

  return null;
}

export function RouteTracking() {
  const location = useLocation();
  const previewWritingIntent = isCourseWritingPreviewRoute(location.pathname, location.search);

  // Layout effects run before every passive effect of the new page, so events
  // a page fires on mount never see a purchase reference in the URL. The page
  // still gets its references from the router and from the set the scrub keeps
  // (src/utils/funnelReturnParams.ts).
  useLayoutEffect(() => {
    scrubCheckoutReturnParamsFromUrl();
  }, [location.pathname, location.search]);

  // journey_timing: start tap-driven journeys and drop ones that left their routes.
  useEffect(() => {
    noteJourneyRoute(location.pathname, location.search);
  }, [location.pathname]); // pathname only: query churn is not a new route

  useEffect(() => {
    // Track page_view on route-level navigations only. Query-string churn
    // (checkout/session params, auth return cleanup) can otherwise inflate
    // GA4 Realtime counts for a single visitor on the same screen.
    const path = location.pathname;
    const normalizedPath = (path.replace(/\/+$/, '') || '/').toLowerCase();
    // Preview navigation never counts as participant session activity. The
    // authenticated route boundary independently authorizes the preview.
    if (previewWritingIntent) return;
    if (AUTH_ROUTE_TRACKING_SUPPRESSED_PATHS.has(normalizedPath)) {
      return;
    }
    if (isJourneyManagedTrackingRoute(path)) {
      return;
    }

    const pageCategory = getRouteTrackingPageCategory(path);
    const isAdminPage = pageCategory === 'admin';
    const skipPlatforms = isAdminPage || pageCategory === 'technical'
      ? PAGE_VIEW_TECHNICAL_SKIP_PLATFORMS
      : undefined;

    let cancelled = false;
    let timeoutId: number | null = null;
    let frameId = 0;

    const flushPageView = () => {
      if (cancelled) return;
      const currentPath = window.location.pathname.replace(/\/+$/, '') || '/';
      const trackedPath = path.replace(/\/+$/, '') || '/';
      if (currentPath !== trackedPath) {
        return;
      }

      const appRouteTitle = getAppRouteTrackingTitle(path, location.search, location.state);
      const pageTitle = appRouteTitle || document.title;

      if (appRouteTitle && document.title !== appRouteTitle) {
        document.title = appRouteTitle;
      }

      // Pixel and analytics SDKs read the page URL on a page view. A reference
      // that reached the URL after the layout effect above (or after the entry
      // scrub) leaves it first.
      scrubCheckoutReturnParamsFromUrl();
      trackPageView(path, pageTitle, {
        skipPlatforms,
        pageCategory,
        isAdminPage,
      });
    };

    frameId = window.requestAnimationFrame(() => {
      frameId = window.requestAnimationFrame(() => {
        timeoutId = window.setTimeout(flushPageView, 0);
      });
    });

    return () => {
      cancelled = true;
      window.cancelAnimationFrame(frameId);
      if (timeoutId !== null) {
        window.clearTimeout(timeoutId);
      }
    };
  }, [location.pathname, previewWritingIntent]);

  return null;
}

function RouteAppearance() {
  const { pathname, search } = useLocation();
  const { syncRouteAppearance } = useTheme();
  useLayoutEffect(() => {
    syncRouteAppearance(pathname, search);
  }, [pathname, search, syncRouteAppearance]);
  return null;
}

function RouteScrollManager() {
  const location = useLocation();

  useEffect(() => {
    if (typeof window === 'undefined') return;
    if (!('scrollRestoration' in window.history)) return;

    const previousSetting = window.history.scrollRestoration;
    window.history.scrollRestoration = 'manual';

    return () => {
      window.history.scrollRestoration = previousSetting;
    };
  }, []);

  useEffect(() => {
    if (typeof window === 'undefined') return;

    // Preserve hash-anchor behavior when a matching element exists.
    if (location.hash) {
      let anchorId = '';
      try {
        anchorId = decodeURIComponent(location.hash.slice(1));
      } catch {
        anchorId = location.hash.slice(1);
      }

      if (anchorId) {
        const target = document.getElementById(anchorId);
        if (target) {
          target.scrollIntoView();
          return;
        }
      }
    }

    try {
      window.scrollTo({ top: 0, left: 0, behavior: 'auto' });
    } catch {
      // jsdom and some embedded browsers may not implement scrollTo options.
      try {
        window.scrollTo(0, 0);
      } catch {
        // no-op
      }
    }

    // Defensive fallback for environments where window scroll APIs are flaky.
    document.documentElement.scrollTop = 0;
    document.body.scrollTop = 0;
  }, [location.pathname, location.search, location.hash]);

  return null;
}

export function RouteSeoPolicy() {
  const location = useLocation();
  const { pathname } = location;

  let canonicalPath: string | null = null;
  let title: string | null = null;
  let robots = 'noindex, follow';
  const isProtectedAppRoute = [
    '/admin',
    '/advisor-portal',
    '/my-programs',
  // React Router matches paths case-insensitively, so /Admin renders the same shell.
  ].some((route) => {
    const path = pathname.toLowerCase();
    return path === route || path.startsWith(`${route}/`);
  });

  if (pathname === '/') {
    canonicalPath = '/';
  } else if (pathname === '/home') {
    canonicalPath = '/';
  } else if (pathname === '/login' || pathname === '/signup') {
    canonicalPath = '/auth';
  } else if (pathname === '/onboarding') {
    canonicalPath = '/';
    title = 'Onboarding | Journal Party';
    robots = 'noindex, nofollow';
  } else if (isProtectedAppRoute) {
    // SSR renders an anonymous shell for protected routes. Emit crawl controls
    // before client auth can redirect so those shells never become indexable.
    canonicalPath = pathname.toLowerCase();
    robots = 'noindex, nofollow';
  }

  if (!canonicalPath) {
    return null;
  }

  return (
    <Helmet>
      {title ? <title>{title}</title> : null}
      <meta name="robots" content={robots} />
      <link rel="canonical" href={`${SITE_URL}${canonicalPath}`} />
    </Helmet>
  );
}

function LegacyAuthRedirect({ mode }: { mode: 'signin' | 'signup' | 'forgot' }) {
  const location = useLocation();
  const params = new URLSearchParams(location.search);
  params.set('mode', mode);

  return <Navigate to={`/auth?${params.toString()}`} replace />;
}

// Fallback for lazy loading
const RouteFallback = () => {
  const nativeDevice = getNativeDeviceProfile();
  const isNativeRuntime = nativeDevice.isNativeRuntime;

  if (isNativeRuntime) {
    return (
      <div
        className="flex min-h-screen items-center justify-center bg-jp-bg-page text-jp-fg-primary"
      >
        <div role="status" aria-live="polite" aria-atomic="true">
          <NeonLoadingRing />
          <span className="sr-only">Loading Journal Party</span>
        </div>
      </div>
    );
  }

  return (
    <div className="min-h-screen bg-jp-bg-page text-jp-fg-primary">
      <div className="mx-auto flex min-h-screen w-full max-w-5xl flex-col gap-5 px-4 py-5 sm:px-6">
        <div className="flex items-center justify-between">
          <div className="h-8 w-32 rounded-lg bg-jp-fg-primary/12" />
          <div className="h-9 w-9 rounded-full bg-jp-bg-card" />
        </div>
        <div className="relative min-h-[46vh] overflow-hidden rounded-[28px] border border-jp-line-edge bg-jp-bg-card shadow-[shadow:var(--jp-shadow-card)]">
          <div className="absolute inset-0 bg-gradient-to-br from-jp-accent-primary/20 via-transparent to-jp-accent-secondary/12" />
          <div className="relative flex h-full min-h-[46vh] flex-col justify-end p-6">
            <div className="mb-5 h-3 w-28 rounded-full bg-jp-accent-primary/70" />
            <div className="h-10 w-4/5 max-w-lg rounded-xl bg-jp-fg-primary/14" />
            <div className="mt-4 h-3 w-full max-w-xl rounded-full bg-jp-fg-secondary/20" />
            <div className="mt-2 h-3 w-2/3 max-w-md rounded-full bg-jp-fg-secondary/16" />
            <div className="mt-7 h-12 w-40 rounded-full bg-jp-accent-primary/75" />
          </div>
        </div>
        <div className="grid gap-3 sm:grid-cols-3">
          <div className="h-24 rounded-2xl border border-jp-line-edge bg-jp-bg-card" />
          <div className="h-24 rounded-2xl border border-jp-line-edge bg-jp-bg-card/90" />
          <div className="h-24 rounded-2xl border border-jp-line-edge bg-jp-bg-card/80" />
        </div>
      </div>
    </div>
  );
};

// Error fallback
const ErrorFallback = ({ error }: { error: unknown }) => {
  const message = error instanceof Error ? error.message : 'An unexpected error occurred.';

  return (
    <div className="flex min-h-screen items-center justify-center bg-v7-bg-primary p-4">
      <div className="text-center">
        <h1 className="text-xl font-bold text-v7-error mb-2">Something went wrong</h1>
        <p className="text-v7-text-secondary">{message}</p>
      </div>
    </div>
  );
};

// Query client
export const createAppQueryClient = () =>
  new QueryClient({
    defaultOptions: {
      queries: {
        staleTime: 1000 * 60, // 1 minute
        retry: 1,
      },
    },
  });

const queryClient = createAppQueryClient();

// Simple Tooltip Provider wrapper (v7 version)
const SimpleTooltipProvider = ({ children }: { children: React.ReactNode }) => {
  return <>{children}</>;
};

// JP reading surfaces are now the canonical PowerPrompt experience.
// The legacy SoloSession / JourneyLearn page modules remain on disk
// only as historical reference and are no longer rendered.


export const AppRoutes = () => (
  <Suspense fallback={<RouteFallback />}>
    <ErrorBoundary>
      <RouteSeoPolicy />
      <NativeRouteFence>
      <Routes>
          {/* Public routes */}
          <Route path="/" element={<NativeAwareRootRoute />} />
          <Route path="/start" element={<GuidedSessionLanding />} />

          {/* Short share links (/r/p, /r/e, /r/j, /r/jp, /r/share). Legacy
              referral codes (system removed) still fall back to "/". */}
          <Route path="/r/*" element={<ShortShareRedirect />} />
          <Route path="/ref/*" element={<ShortShareRedirect />} />

          {/* Auth routes */}
          <Route path="/auth" element={<Auth />} />
          <Route path="/auth/verify" element={<AuthVerify />} />
          <Route path="/auth/callback" element={<AuthCallback />} />
          <Route path="/auth/native-callback" element={<AuthNativeCallback />} />
          <Route path="/auth/confirm-email" element={<AuthConfirmEmail />} />
          <Route path="/check-email" element={<CheckEmail />} />
          <Route path="/login" element={<LegacyAuthRedirect mode="signin" />} />
          <Route path="/signup" element={<LegacyAuthRedirect mode="signup" />} />
          <Route path="/reset-password" element={<Auth />} />
          <Route path="/forgot-password" element={<LegacyAuthRedirect mode="forgot" />} />

          {/* Onboarding route (Epic 7) - protected but skips onboarding check */}
          <Route
            path="/onboarding"
            element={
              <ProtectedRoute skipOnboardingCheck>
                <OnboardingEntry />
              </ProtectedRoute>
            }
          />
          {SessionCompletePreview ? (
            <Route path="/dev/session-complete" element={<SessionCompletePreview />} />
          ) : null}
          {ToastGallery ? <Route path="/dev/toasts" element={<ToastGallery />} /> : null}
          {SessionStartPreview ? <Route path="/dev/session-start" element={<SessionStartPreview />} /> : null}
          {SessionPlayerPreview ? <Route path="/dev/session-player" element={<SessionPlayerPreview />} /> : null}
          {HomePreview ? <Route path="/dev/home" element={<HomePreview />} /> : null}
          {PartyPreview ? <Route path="/dev/party" element={<PartyPreview />} /> : null}
          {PartyLinesPreview ? <Route path="/dev/party-lines" element={<PartyLinesPreview />} /> : null}
          {OnboardingV3Preview ? (
            <Route path="/dev/onboarding-v3" element={<OnboardingV3Preview />} />
          ) : null}
          <Route
            path="/complete-account"
            element={
              <ProtectedRoute skipOnboardingCheck>
                <CompleteAccount />
              </ProtectedRoute>
            }
          />

          {/* Legacy route - redirect to unified Home */}
          <Route
            path="/discover"
            element={<LegacyDiscoverRedirect />}
          />

          {/* Nav v2 Routes (Home | Prompts | Live) */}
          <Route
            path="/home"
            element={
              <ProtectedRoute>
                <NativeShellCoreAccessGate>
                  <Home />
                </NativeShellCoreAccessGate>
              </ProtectedRoute>
            }
          />
          {/* /first-session was a separate primer that duplicated the session
              player's intro (same prompt shown twice). It now routes straight
              into the session, which shows one clear pre-session screen.
              Kept as a route so existing email/notification links still land. */}
          <Route
            path="/first-session"
            element={
              <CoreAccessRoute>
                <Navigate to="/daily-prompt/session?src=first-session" replace />
              </CoreAccessRoute>
            }
          />
          <Route
            path="/daily-prompt/session"
            element={
              <CoreAccessRoute>
                <DailyPromptSession />
              </CoreAccessRoute>
            }
          />
          {/* Freestyle session — the PowerPrompt player without prompts:
              bring your own timer, same sounds and vibe. */}
          <Route
            path="/freestyle"
            element={
              <CoreAccessRoute>
                <CourseWritingPreviewBoundary surface="freestyle"><FreestyleSessionJp /></CourseWritingPreviewBoundary>
              </CoreAccessRoute>
            }
          />
          <Route
            path="/prompts"
            element={<Navigate to="/programs?type=powerprompt" replace />}
          />
          <Route
            path="/live"
            element={<NativeShellCoreAccessGate><Live /></NativeShellCoreAccessGate>}
          />
          <Route path="/journal-party-live" element={<JournalPartyLiveFunnel />} />
          <Route path="/journal-party-live/check-email" element={<JournalPartyLiveCheckEmail />} />
          <Route path="/journal-party-live/confirm" element={<JournalPartyLiveEmailConfirmation />} />
          <Route path="/journal-party-live/welcome" element={<JournalPartyLiveWelcome />} />
          <Route path="/journal-party-live/offer" element={<JournalPartyLiveOffer />} />
          <Route path="/journal-party-live/challenge" element={<JournalPartyLiveChallenge />} />
          <Route path="/challenge" element={<JournalPartyLiveChallenge variant="direct" />} />
          <Route
            path="/journal-party-live/challenge/second-chance"
            element={<JournalPartyLiveChallenge variant="secondChance" />}
          />
          <Route
            path="/journal-party-live/challenge/signup"
            element={<JournalPartyLiveChallenge variant="signup" />}
          />
          <Route
            path="/journal-party-live/challenge/premium-offer"
            element={<ChallengePremiumOffer />}
          />
          <Route
            path="/challenge/premium-offer"
            element={<ChallengePremiumOffer />}
          />
          <Route
            path="/journal-party-live/challenge/premium-downsell"
            element={<ChallengePremiumDownsellOffer />}
          />
          <Route
            path="/challenge/premium-downsell"
            element={<ChallengePremiumDownsellOffer />}
          />
          <Route
            path="/journal-party-live/challenge/confirmation"
            element={<ChallengeOrderConfirmation />}
          />
          <Route
            path="/challenge/confirmation"
            element={<ChallengeOrderConfirmation />}
          />
          <Route
            path="/replays"
            element={
              <SubscriptionRequiredRoute>
                <Replays />
              </SubscriptionRequiredRoute>
            }
          />

          {/* Journey funnels ($19 Journeys, web only; NativeRouteFence sends
              /journal/* to "/" on native). Exact "/journal" stays the legacy
              redirect below. */}
          <Route path="/journal/:seoSlug" element={<JourneyFunnelLanding />} />
          <Route path="/journal/:seoSlug/premium-offer" element={<JourneyFunnelPremiumOffer />} />
          <Route path="/journal/:seoSlug/premium-downsell" element={<JourneyFunnelDownsell />} />
          <Route path="/journal/:seoSlug/confirmation" element={<JourneyFunnelConfirmation />} />

          {/* Legacy route redirects */}
          <Route
            path="/journal"
            element={<Navigate to="/home" replace />}
          />
          <Route
            path="/party"
            element={
              <ProtectedRoute>
                <Party />
              </ProtectedRoute>
            }
          />
          <Route
            path="/parties"
            element={<Navigate to="/live" replace />}
          />
          <Route
            path="/calendar"
            element={<Calendar />}
          />
          <Route
            path="/events/:eventId"
            element={<LegacyEventsRedirect />}
          />
          <Route
            path="/events/:eventId/auto-run"
            element={<LegacyEventsRedirect />}
          />
          <Route
            path="/programs"
            element={<NativeShellCoreAccessGate><Programs /></NativeShellCoreAccessGate>}
          />
          <Route path="/library" element={<LibraryIndex />} />

          {/* Legacy single-event route redirect */}
          <Route
            path="/event/:eventId"
            element={<LegacyEventsRedirect />}
          />

          {/* /event/:eventId room path removed — in-app live rooms disabled (YouTube-centric pivot) */}
          <Route
            path="/profile"
            element={
              <ProtectedRoute>
                <Profile />
              </ProtectedRoute>
            }
          />
          <Route
            path="/settings"
            element={
              <ProtectedRoute>
                <Settings />
              </ProtectedRoute>
            }
          />

          {/* User Schedule (My RSVPs redesign) */}
          <Route
            path="/schedule"
            element={
              <SubscriptionRequiredRoute>
                <MySchedule />
              </SubscriptionRequiredRoute>
            }
          />

          <Route path="/for-creators" element={<ForCreators />} />
          {CREATOR_AUDIENCE_ROUTES.map(audience => (
            <Route key={audience.slug} path={creatorAudiencePath(audience.slug)} element={<ForCreators slug={audience.slug} />} />
          ))}
          <Route path="/journal-prompts" element={<JournalPromptsHub />} />
          <Route path="/daily-journal-prompts" element={<DailyJournalPrompts />} />
          <Route path="/what-to-write-in-a-journal" element={<WhatToWriteInAJournal />} />
          <Route path="/journal-prompt-of-the-day" element={<JournalPromptOfTheDay />} />
          <Route path="/journal-prompt-generator" element={<JournalPromptGenerator />} />
          <Route path="/journal-prompts/for-beginners" element={<JournalPromptPersonaPage />} />
          <Route path="/journal-prompts/for-therapists" element={<JournalPromptPersonaPage />} />
          <Route path="/journal-prompts/for-coaches" element={<JournalPromptPersonaPage />} />
          <Route path="/journal-prompts/for-therapy-clients" element={<JournalPromptPersonaPage />} />
          <Route path="/journal-prompts/for-couples" element={<JournalPromptPersonaPage />} />
          <Route path="/journal-prompts/for-students" element={<JournalPromptPersonaPage />} />
          <Route path="/journal-prompts/for-teens" element={<JournalPromptPersonaPage />} />
          <Route path="/journal-prompts/for-women" element={<JournalPromptPersonaPage />} />
          <Route path="/journal-prompts/for-:personaSlug" element={<JournalPromptPersonaPage />} />
          <Route path="/journal-prompts/august" element={<JournalPromptsMonthPage />} />
          <Route path="/journal-prompts/september" element={<JournalPromptsMonthPage />} />
          <Route path="/journal-prompts/october" element={<JournalPromptsMonthPage />} />
          <Route path="/journal-prompts/november" element={<JournalPromptsMonthPage />} />
          <Route path="/journal-prompts/december" element={<JournalPromptsMonthPage />} />
          <Route path="/journal-prompts/january" element={<JournalPromptsMonthPage />} />
          <Route path="/journal-prompts/new-year" element={<NewYearJournalPrompts />} />
          <Route path="/journal-prompts/:tagSlug" element={<JournalPromptTagPage />} />
          <Route path="/guided-journaling" element={<GuidedJournalingGuide />} />
          <Route path="/guided-journaling-app" element={<GuidedJournalingApp />} />
          <Route path="/private-journaling-app" element={<PrivateJournalingApp />} />
          <Route path="/ai-journaling-alternative" element={<AiJournalingAlternative />} />
          <Route path="/therapist-reviewed-journal-prompts" element={<TherapistReviewedJournalPrompts />} />
          <Route path="/best-guided-journaling-platform" element={<BestGuidedJournalingPlatform />} />
          <Route path="/best-journaling-apps" element={<BestJournalingApps />} />
          <Route path="/journal-prompts-for-anxiety" element={<JournalPromptsForAnxiety />} />
          <Route path="/journaling-for-depression" element={<JournalingForDepression />} />
          <Route path="/gratitude-journaling" element={<GratitudeJournaling />} />
          <Route path="/things-to-be-grateful-for" element={<ThingsToBeGratefulFor />} />
          <Route path="/gratitude-journal-app" element={<GratitudeJournalApp />} />
          <Route path="/journal-prompts-for-mental-health" element={<JournalPromptsForMentalHealth />} />
          <Route path="/journal-prompts-for-self-growth" element={<JournalPromptsForSelfGrowth />} />
          <Route path="/journaling-techniques" element={<JournalingTechniques />} />
          <Route path="/journaling-ideas" element={<JournalingIdeas />} />
          <Route path="/group-journaling" element={<GroupJournaling />} />
          <Route path="/journaling-club" element={<JournalingClub />} />
          <Route path="/journal-sessions" element={<JournalSessions />} />
          <Route path="/journaling-challenge" element={<JournalingChallenge />} />
          <Route path="/best-journaling-app-with-prompts" element={<BestJournalingAppWithPrompts />} />
          <Route path="/faq/app-that-gives-daily-journal-prompts" element={<FaqAppDailyJournalPrompts />} />
          <Route path="/faq/best-app-for-journaling" element={<FaqBestAppForJournaling />} />
          <Route path="/faq/completely-free-journal-apps" element={<FaqCompletelyFreeJournalApps />} />
          <Route path="/faq/does-apple-have-a-journaling-app" element={<FaqDoesAppleHaveJournalingApp />} />
          <Route path="/faq/3-3-3-journal-method" element={<Faq333JournalMethod />} />
          <Route path="/faq/what-can-i-journal-about-every-day" element={<FaqWhatToJournalEveryDay />} />
          <Route path="/faq/what-to-write-journal-for-mental-health" element={<FaqWhatToWriteMentalHealth />} />
          <Route path="/faq/5-minute-journal-prompts" element={<Faq5MinuteJournalPrompts />} />
          <Route path="/faq/3-2-1-journaling-method" element={<Faq321JournalingMethod />} />
          <Route path="/faq/what-to-write-in-journal-every-morning" element={<FaqWhatToWriteInJournalEveryMorning />} />
          <Route path="/faq/good-morning-journal-prompts" element={<FaqGoodMorningJournalPrompts />} />
          {/* Consolidated SEO pages: 301 at the edge (vercel.json); client backstop for in-app nav */}
          <Route path="/journal-prompts-app-for-mental-health" element={<Navigate to="/journal-prompts-for-mental-health" replace />} />
          <Route path="/daily-journaling-prompts-for-mental-health" element={<Navigate to="/journal-prompts-for-mental-health" replace />} />
          <Route path="/journaling-for-anxiety" element={<Navigate to="/journal-prompts-for-anxiety" replace />} />
          <Route path="/365-daily-journal-prompts" element={<ThreeHundredSixtyFiveDailyJournalPrompts />} />
          <Route path="/post/:slug" element={<LegacyPostRedirect />} />

          {/* Premium routes (Epic 8) */}
          {/* /free is the web lead magnet; in the app it sits behind the hard paywall. */}
          <Route path="/free" element={<NativeShellCoreAccessGate><FreePrograms /></NativeShellCoreAccessGate>} />
          <Route path="/links" element={<Links />} />
          <Route path="/upgrade" element={<NativeUpgrade />} />
          <Route path="/pricing" element={<Pricing />} />
          <Route path="/bundle" element={<Navigate to="/pricing" replace />} />
          <Route path="/bundle/thanks" element={<PhysicalBundleThankYou />} />
          <Route
            path="/welcome-premium"
            element={
              <ProtectedRoute skipOnboardingCheck>
                <PremiumWelcome />
              </ProtectedRoute>
            }
          />

          {/* Legal & Info Pages (Phase 26) */}
          <Route path="/terms" element={<Terms />} />
          <Route path="/privacy" element={<Privacy />} />
          <Route path="/about" element={<About />} />
          <Route path="/contact" element={<Contact />} />
          <Route path="/support" element={<Support />} />
          <Route path="/advisory-board" element={<AdvisoryBoard />} />

          {/* Mental Health Advisory Board - Advisor Portal (Phase 53) */}
          <Route
            path="/advisor-portal"
            element={
              <ProtectedRoute>
                <AdvisorPortal />
              </ProtectedRoute>
            }
          />
          <Route
            path="/review/:reviewId"
            element={
              <ProtectedRoute>
                <ReviewDetail />
              </ProtectedRoute>
            }
          />

          {/* Email Unsubscribe (Epic 19) - public route */}
          <Route path="/unsubscribe" element={<Unsubscribe />} />

          {/* Public SEO Landing Pages (Epic 17) */}
          <Route path="/p/:programId" element={<ProgramLanding />} />
          <Route path="/e/:eventId" element={<EventLanding />} />
          <Route
            path={`/j/${FILL_YOUR_JOURNAL_JOURNEY_SLUG}`}
            element={
              <JournalPartyLiveChallenge
                variant="direct"
                offerPathOverride={`/j/${FILL_YOUR_JOURNAL_JOURNEY_SLUG}`}
              />
            }
          />
          <Route path="/j/:journeyId" element={<JourneyLanding />} />
          <Route path="/journeys" element={<Navigate to="/programs?tab=journeys" replace />} />
          <Route path="/course/:slug" element={<Navigate to="/journal-prompts" replace />} />
          <Route path="/challenge/:slug" element={<LegacyChallengeRedirect />} />

          {/* Podcast Pages */}
          <Route path="/podcast" element={<Podcast />} />
          <Route path="/podcast/:episodeNumber" element={<PodcastEpisode />} />

          {/* UGC Share Pages (Phase 5 - À la Carte) */}
          <Route path="/share/program/:slug" element={<ShareProgramRedirect />} />
          <Route path="/share/event/:slug" element={<ShareEventRedirect />} />
          <Route path="/share/journey/:slug" element={<ShareJourneyRedirect />} />

          {/* Social Features (Epic 13) */}
          <Route
            path="/user/:userId"
            element={<UserProfile />}
          />
          <Route
            path="/profile/:userId"
            element={<UserProfile />}
          />
          {/* Short profile link by handle: /u/calm_otter_42 (Oct 7 2026). */}
          <Route
            path="/u/:userId"
            element={<UserProfile />}
          />

          {/* The ranked leaderboard is retired; members' practice stats stay private. */}
          <Route path="/leaderboard" element={<Navigate to="/party" replace />} />

          {/* Collections (Epic 10.2) */}
          <Route
            path="/collection/:collectionId"
            element={
              <ProtectedRoute>
                <CollectionDetail />
              </ProtectedRoute>
            }
          />
          <Route
            path="/search"
            element={
              <SubscriptionRequiredRoute>
                <DiscoveryPrototype />
              </SubscriptionRequiredRoute>
            }
          />

          {/* Program routes (Epic 6) */}
          <Route
            path="/program/:programId"
            element={<ProgramDetailRoute />}
          />
          {/* Solo session route */}
          <Route
            path="/session/:programId"
            element={
              <JourneySessionAccessRoute>
                <SoloSessionJp />
              </JourneySessionAccessRoute>
            }
          />

          {/* Course routes removed, redirect to programs */}
          <Route path="/course/:courseId" element={<Navigate to="/journal-prompts" replace />} />
          <Route path="/course/:courseId/learn" element={<Navigate to="/journal-prompts" replace />} />
          {/* Legacy challenge learn links now resolve to the canonical Journey. */}
          <Route
            path="/challenge/:challengeId/learn"
            element={<LegacyChallengeRedirect learn />}
          />

          {/* Journey routes */}
          <Route path="/journey/:journeyId/start" element={<JourneyEntry />} />
          <Route
            path="/journey/:journeyId"
            element={<JourneyDetailRoute />}
          />
          <Route
            path="/journey/:journeyId/learn"
            element={
              <ProtectedRoute>
                <JourneyLearnJp />
              </ProtectedRoute>
            }
          />

          {/* My Programs — manage your created programs */}
          <Route
            path="/my-programs"
            element={
              <ProtectedRoute>
                <MyPrograms />
              </ProtectedRoute>
            }
          />

          {/* Legacy redirects */}
          <Route
            path="/creator"
            element={<Navigate to="/my-programs" replace />}
          />

          {/* /create: members who can create programs go to the editor, everyone else to My programs. */}
          <Route
            path="/create"
            element={
              <ProtectedRoute>
                <Create />
              </ProtectedRoute>
            }
          />
          <Route
            path="/create/program"
            element={
              <ProgramAuthoringRoute>
                <CreateProgram />
              </ProgramAuthoringRoute>
            }
          />
          <Route
            path="/create/program/:programId"
            element={
              <ProgramAuthoringRoute>
                <CreateProgram />
              </ProgramAuthoringRoute>
            }
          />
          <Route
            path="/create/program/:programId/prompts"
            element={
              <ProgramAuthoringRoute>
                <CreateProgramPrompts />
              </ProgramAuthoringRoute>
            }
          />
          {/* /create/event removed — user event creation disabled (YouTube-centric pivot) */}
          {/* Journey authoring is held back for admins only (Oct 2026); the server decides. */}
          <Route path="/create/journeys" element={<JourneyAuthoringRoute><CreatorJourneys /></JourneyAuthoringRoute>} />
          <Route path="/create/journey/:journeyId" element={<JourneyAuthoringRoute><CourseJourneyEdit /></JourneyAuthoringRoute>} />
          <Route path="/create/affiliate" element={<Navigate to="/my-programs" replace />} />

          {/* Admin routes (Epic 9) - role check in AdminLayout */}
          <Route
            path="/admin"
            element={
              <ProtectedRoute>
                <AdminLayout />
              </ProtectedRoute>
            }
          >
            <Route index element={<AdminToday />} />
            <Route path="dashboard" element={<Navigate to="/admin" replace />} />
            <Route path="library" element={<AdminLibrary />} />
            <Route path="programs" element={<ProgramsListRedirect />} />
            <Route path="programs/new" element={<AdminProgramEdit />} />
            <Route path="programs/:programId" element={<AdminProgramEdit />} />
            <Route path="collections" element={<AdminCollections />} />
            <Route path="reports" element={<AdminReports />} />
            <Route path="events" element={<AdminEvents />} />
            <Route path="events/new" element={<AdminEventEdit />} />
            <Route path="events/:eventId" element={<AdminEventEdit />} />
            <Route path="people" element={<AdminPeople />} />
            <Route path="users" element={<Navigate to="/admin/people" replace />} />
            <Route path="users/:userId" element={<AdminUserRedirect />} />
            <Route path="journeys" element={<AdminJourneys />} />
            <Route path="journey-builder/:journeyId" element={<CourseJourneyEdit admin />} />
            <Route path="journeys/new" element={<AdminJourneyEdit />} />
            <Route path="journeys/:journeyId" element={<AdminJourneyEdit />} />
            <Route path="courses/*" element={<Navigate to="/admin/journeys" replace />} />
            <Route path="challenges/*" element={<Navigate to="/admin/journeys" replace />} />
            <Route path="course-programs/:programId" element={<Navigate to="/admin/journeys" replace />} />
            <Route path="signature" element={<Navigate to="/admin/events?type=signature" replace />} />
            <Route path="growth/*" element={<AdminGrowth />} />
            <Route path="analytics" element={<Navigate to="/admin/growth" replace />} />
            <Route path="funnel-analytics" element={<Navigate to="/admin/growth/funnels" replace />} />
            <Route path="messages/*" element={<AdminMessages />} />
            <Route path="advisory-board" element={<AdvisoryBoardRedirect />} />
            <Route path="notifications" element={<Navigate to="/admin/messages/notifications" replace />} />
            <Route path="notification-monitor" element={<Navigate to="/admin/messages/notifications" replace />} />
            <Route path="money" element={<AdminMoney />} />
            <Route path="revenue" element={<Navigate to="/admin/money" replace />} />
            <Route path="settings" element={<AdminSettings />} />
            <Route path="marketing" element={<AdminMarketing />} />
            <Route path="emails/*" element={<EmailsRedirect />} />
            <Route path="attribution-health" element={<Navigate to="/admin/growth/attribution" replace />} />
            <Route path="*" element={<Navigate to="/admin" replace />} />
          </Route>

          {/* 404 - Page not found */}
          <Route path="*" element={<NotFound />} />
      </Routes>
      </NativeRouteFence>
    </ErrorBoundary>
  </Suspense>
);

export const AppProviders = ({
  children,
  queryClient: providedClient,
  includeRoutePrefetcher = true,
  isSSR = false,
  helmetContext,
  ssrData,
}: {
  children: ReactNode;
  queryClient?: QueryClient;
  includeRoutePrefetcher?: boolean;
  isSSR?: boolean;
  helmetContext?: object;
  ssrData?: AppSsrData | null;
}) => {
  const client = providedClient ?? queryClient;
  const nativeDevice = getNativeDeviceProfile();
  const shouldDisableRoutePrefetching = nativeDevice.isNativePhone;
  const resolvedSsrData =
    ssrData ?? (typeof window !== 'undefined' ? window.__JP_SSR_DATA__ ?? null : null);
  const [hasMounted, setHasMounted] = useState(false);
  // Before the first render, so the server and the hydration render read the
  // same flag answer (no Premium-then-VIP switch on SSR pages).
  useState(() => seedRemoteFlagCache(client, resolvedSsrData?.remoteFlags));

  useEffect(() => {
    // Transition: during SSR hydration the lazy route's Suspense boundary may
    // still be dehydrated; a plain setState here forces React to abandon the
    // server HTML and client-render (minified error #421).
    startTransition(() => {
      setHasMounted(true);
    });
  }, []);

  // SSR-safe render: skip browser-only components (Sentry, MarketingScripts, Toaster, CookieConsent)
  // Provides a stub AuthContext so components calling useAuth() don't crash
  if (isSSR) {
    const noop = () => Promise.resolve({ error: null });
    const ssrAuthValue = {
      user: null, session: null, profile: null, loading: false, isLoading: false, profileLoading: false, profileLoadFailed: false, isRecovering: false,
      authStartupFailed: false, retryAuthStartup: () => undefined,
      signIn: noop, signUp: noop, signInWithGoogle: noop, signInWithApple: noop, signOut: noop,
      startAnonymousSession: noop,
      connectAnonymousWithProvider: () => Promise.resolve({ error: null, connected: false }),
      requestAnonymousEmailVerification: noop,
      verifyAnonymousEmail: noop,
      refreshAnonymousEmailVerification: () => Promise.resolve({ error: null, connected: false }),
      requestEmailSignInCode: noop,
      verifyEmailSignInCode: noop,
      resendVerificationEmail: noop,
      resetPassword: noop, updatePassword: noop, updateEmail: noop, refreshProfile: () => Promise.resolve(),
    };
    return (
      <QueryClientProvider client={client}>
        <AppSsrDataProvider data={resolvedSsrData}>
          <AuthContext.Provider value={ssrAuthValue}>
            <HelmetProvider context={helmetContext || {}}>
              <VipPlanNameProvider>{children}</VipPlanNameProvider>
            </HelmetProvider>
          </AuthContext.Provider>
        </AppSsrDataProvider>
      </QueryClientProvider>
    );
  }

  return (
    <Sentry.ErrorBoundary fallback={({ error }) => <ErrorFallback error={error} />}>
      <QueryClientProvider client={client}>
        <AppSsrDataProvider data={resolvedSsrData}>
          <AuthProvider>
            <HelmetProvider>
              <ThemeProvider>
                <JpThemeProvider>
                  <SimpleTooltipProvider>
                    <VipPlanNameProvider>{children}</VipPlanNameProvider>
                    {hasMounted ? <MarketingScripts /> : null}
                    {hasMounted ? <Toaster /> : null}
                    {hasMounted ? (
                      <Suspense fallback={null}>
                        <VipRescueHost />
                      </Suspense>
                    ) : null}
                    {hasMounted ? (
                      <SonnerToaster
                        position="top-right"
                        closeButton
                        // Same Paper + Ink look as the app's own toasts (no stock colors). Sonner
                        // animates and stacks its own toasts, so no jp-toast entrance here.
                        toastOptions={{
                          unstyled: true,
                          classNames: {
                            toast: 'group grid w-[min(380px,calc(100vw-2rem))] grid-cols-[auto_minmax(0,1fr)_auto] items-center gap-3 rounded-[20px] bg-jp-ink py-3 pl-3 pr-2 text-jp-bg-page shadow-[0_14px_34px_-16px_rgba(11,16,32,0.55)] dark:bg-jp-bg-elevated dark:text-jp-fg-primary dark:ring-1 dark:ring-jp-line-edge',
                            title: 'font-jp-display text-[15px] font-bold leading-snug',
                            description: 'mt-0.5 font-jp-body text-[13.5px] leading-snug opacity-75',
                            actionButton: 'font-jp-body text-[13.5px] font-semibold text-jp-accent-primary',
                            closeButton: 'opacity-60 hover:opacity-100',
                            // Sonner sizes [data-icon] to 16px; the mark is 36px, so the wrapper has to match.
                            icon: 'm-0 h-9 w-9',
                          },
                        }}
                        icons={{
                          success: <ToastMark variant="success" />,
                          info: <ToastMark variant="info" />,
                          warning: <ToastMark variant="warning" />,
                          error: <ToastMark variant="error" />,
                        }}
                      />
                    ) : null}
                    {hasMounted ? <CookieConsentBanner /> : null}
                    {hasMounted ? <ConfirmEmailBanner /> : null}
                    {hasMounted && includeRoutePrefetcher && !shouldDisableRoutePrefetching ? <RoutePrefetcher /> : null}
                  </SimpleTooltipProvider>
                </JpThemeProvider>
              </ThemeProvider>
            </HelmetProvider>
          </AuthProvider>
        </AppSsrDataProvider>
      </QueryClientProvider>
    </Sentry.ErrorBoundary>
  );
};

const AppContent = () => {
  const [hasMounted, setHasMounted] = useState(false);

  useEffect(() => {
    // Transition: during SSR hydration the lazy route's Suspense boundary may
    // still be dehydrated; a plain setState here forces React to abandon the
    // server HTML and client-render (minified error #421).
    startTransition(() => {
      setHasMounted(true);
    });
  }, []);

  return (
    <BrowserRouter future={{ v7_startTransition: true, v7_relativeSplatPath: true }}>
      <SsrDataNavigationObserver />
      <RouteAppearance />
      <ReturningSessionGate />
      <RouteTracking />
      <RouteScrollManager />
      <AcquisitionCapture />
      <NativeAccountReminder />
      <CoreAccessAuthSync />
      {/* Inside the router: the fallback ATT prompt waits until the member is
          past first-run routes, which needs the current location. */}
      {hasMounted ? <NativeTrackingBootstrap /> : null}
      <MinVersionGate>
        <AppRoutes />
      </MinVersionGate>
      {hasMounted ? <CharlaWidget /> : null}
    </BrowserRouter>
  );
};

const App = () => (
  <AppProviders>
    <AppContent />
  </AppProviders>
);

export default App;
